import { Countdown } from "@/components/site/Countdown";
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Truck, RotateCcw, ShieldCheck } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api, img, money } from "@/lib/api";
import { useShop } from "@/context/ShopContext";
import { QtyControl } from "@/components/site/BagDrawer";
import useSeo from "@/lib/useSeo";

const Gallery = ({ images, name }) => (
  <>
    <div data-testid="product-gallery-mobile" className="lg:hidden flex overflow-x-auto snap-x snap-mandatory no-scrollbar">
      {images.map((src, i) => <img key={i} src={img(src)} alt={`${name} ${i + 1}`} className="snap-center shrink-0 w-[88%] aspect-[3/4] object-cover bg-[var(--mm-sand)] mr-1" />)}
    </div>
    <div data-testid="product-gallery" className="hidden lg:grid grid-cols-2 gap-1">
      {images.map((src, i) => (
        <div key={i} className={`overflow-hidden bg-[var(--mm-sand)] img-zoom ${i === 0 ? "col-span-2 aspect-[4/5]" : "aspect-[3/4]"}`}>
          <img src={img(src)} alt={`${name} ${i + 1}`} className="w-full h-full object-cover" />
        </div>
      ))}
    </div>
  </>
);

const StockLabel = ({ qty, size }) => {
  if (!size) return <p data-testid="stock-status" className="text-xs text-[var(--mm-muted)]">Select a size to see availability</p>;
  if (qty <= 0) return <p data-testid="stock-status" className="text-xs text-[var(--mm-red)]">Sold out in size {size}</p>;
  if (qty <= 3) return <p data-testid="stock-status" className="text-xs text-[var(--mm-red)]">Only {qty} left in size {size}</p>;
  return <p data-testid="stock-status" className="text-xs">In stock — ready to ship</p>;
};

export default function ProductPage() {
  const { slug } = useParams();
  const { add, settings } = useShop();
  const linkMode = settings?.payment_mode === "square_links";
  const [p, setP] = useState(null);
  const [error, setError] = useState(false);
  const [size, setSize] = useState("");
  const [colour, setColour] = useState("");
  const [qty, setQty] = useState(1);
  useSeo(p?.name, p?.description?.slice(0, 155));

  useEffect(() => {
    setP(null); setError(false); setSize(""); setQty(1);
    api.get(`/products/${slug}`).then((r) => { setP(r.data); setColour(r.data.colours?.[0]?.name || ""); }).catch(() => setError(true));
  }, [slug]);

  if (error) return <div className="px-6 py-32"><p className="font-serif-mm text-5xl">This piece has <em className="text-[var(--mm-red)]">moved on.</em></p><Link to="/shop" className="btn-mm mt-8">Shop all</Link></div>;
  if (!p) return <div className="grid lg:grid-cols-2 gap-10 px-6 py-10"><div className="aspect-[3/4] bg-[var(--mm-sand)] animate-pulse" /><div className="h-64 bg-[var(--mm-sand)] animate-pulse" /></div>;

  const pre = !!p.preorder;
  const stock = pre ? 99 : size ? p.stock?.[size] || 0 : 0;
  const addToBag = () => {
    if (!size) { toast.error("Please select a size"); return; }
    add({ product_id: p.id, slug: p.slug, name: p.name, price: p.price, image: p.images?.[0], size, colour, quantity: Math.min(qty, stock), max: stock, preorder: pre });
  };
  const payWithSquare = () => {
    if (!size) { toast.error("Please select a size"); return; }
    window.location.href = p.square_link;
  };

  return (
    <div data-testid="product-page" className="lg:grid lg:grid-cols-12 lg:gap-12 lg:px-10 lg:pt-6 pb-24">
      <div className="lg:col-span-7"><Gallery images={p.images?.length ? p.images : [""]} name={p.name} /></div>
      <div className="lg:col-span-5 px-5 sm:px-10 lg:px-0">
        <div className="lg:sticky lg:top-36 pt-8 lg:pt-4 space-y-7">
          <div>
            <p className="eyebrow text-[var(--mm-red)] mb-3"><Link to={`/shop?category=${encodeURIComponent(p.category)}`}>{p.category}</Link>{p.is_new && " · New"}</p>
            <h1 data-testid="product-name" className="font-serif-mm text-4xl sm:text-5xl leading-[1.05]">{p.name}</h1>
            <p data-testid="product-price" className="mt-3 text-lg">{money(p.price)}</p>
            <p className="text-[0.7rem] text-[var(--mm-muted)] mt-1">Tax included. Free UK delivery over £200.</p>
          </div>
          {p.colours?.length > 0 && (
            <div>
              <p className="eyebrow mb-3">Colour — <span className="text-[var(--mm-muted)] normal-case tracking-normal font-normal text-xs">{colour}</span></p>
              <div className="flex gap-3">
                {p.colours.map((c) => (
                  <button key={c.name} data-testid={`colour-option-${c.name.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setColour(c.name)} title={c.name} className={`w-9 h-9 rounded-full p-[3px] border transition-colors ${colour === c.name ? "border-[var(--mm-ink)]" : "border-transparent"}`}>
                    <span className="block w-full h-full rounded-full" style={{ background: c.hex }} />
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <div className="flex justify-between mb-3"><p className="eyebrow">Size</p><Link to="/size-guide" data-testid="size-guide-link" className="text-xs underline underline-offset-4 text-[var(--mm-muted)] hover:text-[var(--mm-red)]">Size guide</Link></div>
            <div className="grid grid-cols-5 gap-2">
              {p.sizes.map((s) => {
                const out = !pre && (p.stock?.[s] || 0) <= 0;
                return (
                  <button key={s} data-testid={`size-option-${s}`} disabled={out} onClick={() => { setSize(s); setQty(1); }}
                    className={`py-3 text-xs tracking-[0.15em] border transition-colors ${size === s ? "bg-[var(--mm-ink)] text-[var(--mm-cream)] border-[var(--mm-ink)]" : "border-[var(--mm-line)] hover:border-[var(--mm-ink)]"} ${out ? "line-through opacity-35 cursor-not-allowed" : ""}`}>{s}</button>
                );
              })}
            </div>
            <div className="mt-3">{pre ? <p data-testid="preorder-status" className="text-xs text-[var(--mm-red)]">Pre-order now{p.preorder_note ? ` — ${p.preorder_note}` : ""}. Payment is taken today to secure your piece.</p> : <StockLabel qty={stock} size={size} />}</div>
            {pre && <div className="mt-4"><Countdown compact to={settings?.launch_at} title={settings?.launch_title ? "Launching in" : undefined} /></div>}
          </div>
          <div className="flex gap-3 items-stretch">
            {linkMode ? (
              <button data-testid="pay-with-square-button" onClick={payWithSquare} disabled={!p.square_link || (size && stock <= 0)} className="btn-mm flex-1">
                {!p.square_link ? "Coming soon" : size && stock <= 0 ? "Sold out" : `${pre ? "Pre-order" : "Buy now"} · ${money(p.price)}`}
              </button>
            ) : (<>
              <QtyControl testId="product-qty" value={qty} onChange={(q) => setQty(Math.max(1, Math.min(q, stock || 1)))} />
              <button data-testid="add-to-bag-button" onClick={addToBag} disabled={size && stock <= 0} className="btn-mm flex-1">{size && stock <= 0 ? "Sold out" : pre ? "Pre-order" : "Add to Bag"} · {money(p.price * qty)}</button>
            </>)}
          </div>
          {linkMode && p.square_link && <p data-testid="square-size-note" className="text-xs text-[var(--mm-muted)] -mt-4">You'll pay securely with Square. Please add your size{size ? ` (${size})` : ""} in the order note on the payment page.</p>}
          <div className={`grid ${settings?.delivery_badge ? "grid-cols-3" : "grid-cols-2"} gap-2 text-[0.68rem] text-[var(--mm-muted)] border-y border-[var(--mm-line)] py-4`}>
            {settings?.delivery_badge && <span data-testid="delivery-badge" className="flex items-center gap-2"><Truck size={14} className="text-[var(--mm-red)]" /> {settings.delivery_badge}</span>}
            <span className="flex items-center gap-2"><RotateCcw size={14} className="text-[var(--mm-red)]" /> 14-day returns</span>
            <span className="flex items-center gap-2"><ShieldCheck size={14} className="text-[var(--mm-red)]" /> Secure checkout</span>
          </div>
          <Accordion type="multiple" defaultValue={["desc"]} className="w-full">
            <AccordionItem value="desc"><AccordionTrigger data-testid="accordion-description" className="eyebrow hover:no-underline">Description</AccordionTrigger><AccordionContent data-testid="product-description" className="text-sm leading-relaxed text-[var(--mm-muted)]">{p.description}</AccordionContent></AccordionItem>
            {p.details && <AccordionItem value="details"><AccordionTrigger data-testid="accordion-details" className="eyebrow hover:no-underline">Details & Care</AccordionTrigger><AccordionContent><ul className="text-sm text-[var(--mm-muted)] space-y-1.5">{p.details.split("\n").filter(Boolean).map((d) => <li key={d}>— {d}</li>)}</ul></AccordionContent></AccordionItem>}
            <AccordionItem value="ship"><AccordionTrigger data-testid="accordion-shipping" className="eyebrow hover:no-underline">Delivery & Returns</AccordionTrigger><AccordionContent className="text-sm text-[var(--mm-muted)] leading-relaxed">{settings?.delivery_details && <span className="block mb-2 whitespace-pre-line">{settings.delivery_details}</span>}Returns accepted within 14 days of delivery. <Link to="/policies/returns" className="underline">Read more</Link></AccordionContent></AccordionItem>
          </Accordion>
        </div>
      </div>
    </div>
  );
}
